import { supabase } from "../supabaseClient";
import { createTtlCache } from "./ttlCache";


const cache = createTtlCache(60_000);


/**
 * O botão "copiar link" do SoundCloud cola parâmetros de campanha na URL.
 * Eles poluem o link e sujam a analytics do destino, então saem fora.
 */
const CLIPBOARD_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

export const cleanExternalUrl = (url) => {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    CLIPBOARD_PARAMS.forEach((p) => parsed.searchParams.delete(p));
    return parsed.toString().replace(/\?$/, "");
  } catch {
    return url; // não é URL válida (ex: recado interno digitado na planilha)
  }
};


/**
 * Extrai o ID de um vídeo do YouTube aceitando as duas formas que aparecem
 * na planilha: youtu.be/<id> e youtube.com/watch?v=<id>.
 */
const youtubeVideoId = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "youtu.be") return parsed.pathname.slice(1) || null;
    if (host.endsWith("youtube.com")) return parsed.searchParams.get("v");
    return null;
  } catch {
    return null;
  }
};


/**
 * Capa das gravações do YouTube derivada do próprio link — ninguém precisa
 * subir imagem para os vídeos. `hqdefault` existe para todo vídeo público.
 */
export const youtubeThumbnail = (url) => {
  const id = youtubeVideoId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};


/** Onde a gravação mora — o card muda de tratamento visual conforme isso. */
const mediaSource = (url) => {
  if (youtubeVideoId(url)) return "youtube";
  if (url && /(^|\.)soundcloud\.com/.test(safeHost(url))) return "soundcloud";
  return "other";
};

const safeHost = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
};


const mapItem = (item) => {
  const external_url = cleanExternalUrl(item.external_url);
  const source = item.type === "media" ? mediaSource(external_url) : null;

  return {
    id: item.id,
    name: item.name,
    description: item.description,
    type: item.type,
    // imagem cadastrada vence; senão tenta derivar do YouTube; senão null
    image_url: item.image_url || (source === "youtube" ? youtubeThumbnail(external_url) : null),
    source,
    external_url,
    event_date: item.event_date,
  };
};


/**
 * Item desativado (`active = false`) já é barrado pela policy de RLS, então
 * nem chega aqui. O filtro abaixo é redundante de propósito: se alguém
 * afrouxar a policy um dia, o site não passa a exibir rascunho sozinho.
 */
const ativo = (item) => item.active !== false;


const fetchHighlights = async (force) => {
  if (!supabase) throw new Error("Supabase client not configured");

  if (!force) {
    const cached = cache.read();
    if (cached) return cached;
  }

  const { data, error } = await supabase
    .from("highlights")
    .select("*")
    .eq("active", true)
    .order("sort_order", { ascending: true });

  if (error) throw error;

  const result = (data || []).filter(ativo).map(mapItem);
  cache.write(result);

  return result;
};


/**
 * Um ritual só é "próximo" enquanto não passou. Comparação feita no fuso
 * local a partir da meia-noite de hoje, para o evento continuar listado
 * durante o próprio dia em que acontece.
 *
 * Exportada só para teste — não é chamada de fora deste módulo.
 */
export const isUpcoming = (item) => {
  if (!item.event_date) return true; // sem data → o Lucas controla pelo `active`
  const [y, m, d] = item.event_date.split("-").map(Number);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(y, m - 1, d) >= today;
};


/**
 * As três seções da página de Destaques. O agrupamento vem do enum
 * `highlight_type` — não há campo de seção em lugar nenhum.
 */
export const listDestaques = async ({ force = false } = {}) => {
  const items = await fetchHighlights(force);

  return {
    rituais: items
      .filter((i) => i.type === "event" && isUpcoming(i))
      .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || "")),
    merch: items.filter((i) => i.type === "product"),
    gravacoes: items.filter((i) => i.type === "media"),
  };
};
